"use client";

import Link from "next/link";
import { motion } from "motion/react";
import Image from "next/image";
import {
  ArrowRight,
  Shield,
  Clock,
  Zap,
  Instagram,
  Star,
  Check,
} from "lucide-react";

export default function HomePage() {
  return (
    <main className="relative min-h-screen overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-neon-purple/20 via-dark-surface to-neon-pink/10" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-neon-purple/30 rounded-full blur-[150px] -z-10" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-neon-pink/20 rounded-full blur-[120px] -z-10" />

      <nav className="relative z-10 flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
        <Link href="/" className="flex items-center gap-2">
          <Image src="/unicorn-icon.png" alt="Stashds" width={40} height={40} className="rounded-xl" />
          <span className="font-display text-xl font-bold">Stashds</span>
        </Link>
        <div className="hidden md:flex items-center gap-8">
          <Link
            href="/pricing"
            className="text-white/70 hover:text-white transition-colors"
          >
            Pricing
          </Link>
          <Link
            href="/login"
            className="text-white/70 hover:text-white transition-colors"
          >
            Log in
          </Link>
          <Link href="/register" className="btn-neon">
            Get Started <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </nav>

      <section className="relative z-10 px-6 pt-20 pb-32 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center max-w-4xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8">
            <Star className="w-4 h-4 text-cyber-lime" />
            <span className="text-sm text-white/80">
              Trusted by 10,000+ creators
            </span>
          </div>

          <h1 className="text-display-lg md:text-[5rem] font-display font-bold leading-[1.1] mb-6">
            Never lose an{" "}
            <span className="gradient-neon-text">Instagram story</span> again
          </h1>

          <p className="text-xl text-white/60 max-w-2xl mx-auto mb-10">
            Save Instagram stories before they vanish. Archive forever.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/register" className="btn-neon text-lg px-8 py-4">
              Start archiving free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link
              href="/pricing"
              className="btn-glass text-lg px-8 py-4"
            >
              View pricing
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-20 relative"
        >
          <div className="glass-card-elevated p-4 max-w-5xl mx-auto">
            <div className="aspect-video rounded-xl bg-gradient-to-br from-dark-elevated to-dark-surface flex items-center justify-center">
              <div className="text-center">
                <Instagram className="w-16 h-16 text-neon-purple mx-auto mb-4" />
                <p className="text-white/40">Dashboard Preview</p>
              </div>
            </div>
          </div>
          <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-3/4 h-20 bg-neon-purple/20 rounded-full blur-3xl" />
        </motion.div>
      </section>

      <section className="relative z-10 px-6 py-24 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-display font-display font-bold mb-4">
            Why choose Stashds?
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            The most reliable way to save Instagram content
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Clock,
              title: "24/7 Auto-Archive",
              description:
                "Set it and forget it. We automatically save stories before they expire.",
              color: "neon-purple",
            },
            {
              icon: Shield,
              title: "Private & Secure",
              description:
                "Your data is encrypted and stored securely. We never share your archives.",
              color: "neon-blue",
            },
            {
              icon: Zap,
              title: "Lightning Fast",
              description:
                "Download stories instantly. Bulk export hundreds of files in seconds.",
              color: "neon-pink",
            },
          ].map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="card-glass p-8 hover:bg-white/10 transition-all duration-300 group"
            >
              <div
                className={`w-14 h-14 rounded-2xl bg-${feature.color}/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}
              >
                <feature.icon className={`w-7 h-7 text-${feature.color}`} />
              </div>
              <h3 className="text-xl font-display font-semibold mb-3">
                {feature.title}
              </h3>
              <p className="text-white/60">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="relative z-10 px-6 py-24 max-w-7xl mx-auto">
        <div className="glass-card-elevated p-12 text-center">
          <h2 className="text-display-sm font-display font-bold mb-4">
            Ready to save your stories?
          </h2>
          <p className="text-xl text-white/60 mb-8 max-w-xl mx-auto">
            Join thousands of creators who trust Stashds to preserve their
            Instagram memories.
          </p>
          <Link href="/register" className="btn-neon text-lg px-10 py-4">
            Get started for free
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
          <div className="flex items-center justify-center gap-6 mt-8 text-sm text-white/50">
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-cyber-lime" /> No credit card
              required
            </span>
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-cyber-lime" /> 5GB free storage
            </span>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/10 px-6 py-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <Image src="/unicorn-icon.png" alt="Stashds" width={32} height={32} className="rounded-lg" />
            <span className="font-display font-semibold">Stashds</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-white/50">
            <Link href="/pricing" className="hover:text-white transition-colors">
              Pricing
            </Link>
            <Link href="/login" className="hover:text-white transition-colors">
              Login
            </Link>
          </div>
          <p className="text-sm text-white/40">
            &copy; {new Date().getFullYear()} Stashds. All rights reserved.
          </p>
        </div>
      </footer>
    </main>
  );
}
